/* eslint-disable max-len, no-underscore-dangle */
import { isNull } from 'lodash';
import { INFRA_GETTERS, INFRA_STORE_VALUES, ROOT_GETTER_PATHS } from '@core/store';
import { getLastTimeUpdated, getSiteSettingValue } from '@core/utils/utils';
import {
  // MAP_WIDGETS_NUMBER_OF_ROWS_PER_PAGE,
  WIDGET_LOGICAL_NAMES,
  METRIC_NAMES,
  DATA_LOADING_STATES,
  SESSION_SETTINGS,
  FEATURES,
  SORT_DIRECTION,
} from '@config/Consts';

// Use in cases BRAND_NAME or INTENTS column is shown (but not both)
const featureColumnsMinWidth = {
  [METRIC_NAMES.CONVERSATIONS_RESPONSE_TIME]: '90px',
  [METRIC_NAMES.VISITOR_NAME]: '90px',
  [METRIC_NAMES.AGENT_NAME]: '94px',
  [METRIC_NAMES.AGENT_GROUP]: '90px',
  [METRIC_NAMES.CONVERSATION_SKILL]: '90px',
};
// Use in cases both BRAND_NAME and INTENTS columns are shown
const twoFeatureColumnsMinWidth = {
  [METRIC_NAMES.CONVERSATIONS_RESPONSE_TIME]: '85px',
  [METRIC_NAMES.VISITOR_NAME]: '80px',
  [METRIC_NAMES.BRAND_NAME]: '75px',
  [METRIC_NAMES.AGENT_NAME]: '85px',
  [METRIC_NAMES.AGENT_GROUP]: '80px',
  [METRIC_NAMES.CONVERSATION_SKILL]: '75px',
};

const _getDataStatus = state => state.dataStatus;
const getTimeFrameFilter = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_TIMEFRAME_FILTER];
const getUserIds = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_USERS];
const getUsers = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_USERS];
const getFilteredGroupsIds = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GROUPS_FILTER];
const getBlockConsumerName = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_BLOCK_CONSUMER_NAME];
const getSkillsFilter = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_SKILLS_IDS];
const getConversations = state => state.conversations || [];
const hasContactCenterFeature = (state, getters, rootState, rootGetters) =>
  rootGetters[INFRA_GETTERS.FEATURE_STATE](FEATURES.CONTACT_CENTER);
const hasLiveIntent = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_LIVE_INTENT];
const getMetricAttributes = (state, getters, rootState, rootGetters) => {
  const metricAttributes = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_WIDGET_METRIC_ATTRIBUTES](WIDGET_LOGICAL_NAMES.CONVERSATIONS);
  // If this is a ContactCenter account (Rollover) or LiveIntent or both then update the metricAttributes
  let relevantMinWidthObject = {};
  if (hasContactCenterFeature(state, getters, rootState, rootGetters) &&
    hasLiveIntent(state, getters, rootState, rootGetters)) {
    relevantMinWidthObject = twoFeatureColumnsMinWidth;
  } else if (hasContactCenterFeature(state, getters, rootState, rootGetters) ||
    hasLiveIntent(state, getters, rootState, rootGetters)) {
    relevantMinWidthObject = featureColumnsMinWidth;
  }

  for (let i = 0; i < metricAttributes.length; i += 1) {
    const minWidth = relevantMinWidthObject[metricAttributes[i].metricName];
    if (minWidth) {
      metricAttributes[i].baseColumnSize.width =
        '11%';
      metricAttributes[i].baseColumnSize.minWidth =
        minWidth;
    }
  }
  return metricAttributes;
};
const getAgentsFilter = state => state.filter.agents;
const getStatusFilter = state => state.filter.status;
const getSort = state => state.sort;
const getGlobalFiltersUpdating = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GLOBAL_FILTERS_UPDATING][WIDGET_LOGICAL_NAMES.CONVERSATIONS];
const dataHasBeenFetched = state => !isNull(state.conversations);
const isDataObsolete = state =>
  state.dataStatus === DATA_LOADING_STATES.OBSOLETE ||
  (state.dataStatus === DATA_LOADING_STATES.ERROR && dataHasBeenFetched((state)));
const isWaitingForData = (state, getters, rootState, rootGetters) =>
  state.pagination.isLoading ||
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === DATA_LOADING_STATES.LOADING
  || _getDataStatus(state) === DATA_LOADING_STATES.LOADING;
const conversationsApiIsDown = (state, getters, rootState, rootGetters) =>
  (rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === DATA_LOADING_STATES.ERROR
    || _getDataStatus(state) === DATA_LOADING_STATES.ERROR)
  && !dataHasBeenFetched(state);
const lastTimeDataUpdated = state => (isDataObsolete(state) ? getLastTimeUpdated(state.lastTimeDataUpdated) : null);
const getHourFormatSiteSetting = (state, getters, rootState, rootGetters) => {
  const siteSetting = rootGetters[INFRA_GETTERS.SITE_SETTINGS]()[SESSION_SETTINGS.HOUR_FORMAT];
  return getSiteSettingValue({ siteSetting });
};
const getLoggedInUserId = (state, getters, rootState, rootGetters) =>
  rootGetters[INFRA_GETTERS.GET_VALUE](INFRA_STORE_VALUES.USER_ID);

const getAccountId = (state, getters, rootState, rootGetters) =>
  rootGetters[INFRA_GETTERS.GET_VALUE](INFRA_STORE_VALUES.ACCOUNT_ID);

const getLiveIntentsDataStatus = state => state.intents.dataStatus;

const getLiveIntents = (state) => {
  let allIntents = [];
  const domains = state.intents.intentsPerDomain;
  domains.forEach((domain) => {
    allIntents = allIntents.concat(domain.intents);
  });
  return allIntents;
};

const isIntentsFilterEnabled = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_INTENTS_FILTER];


const getLiveIntentsFilter = state => state.filter.liveIntentIds;

const getConversationStatus = (state, getters, rootState) => (convId) => {
  const conversation = state.conversations.find(c => c.conversationId === convId);
  return (conversation && conversation.status) || rootState.amd.transcript.conversation.status;
};

const getPresetValues = (state, getters) => ({
  agentsFilter: getters.getAgentsFilter || {},
  statusFilter: getters.getStatusFilter || null,
  liveIntentsFilter: getters.getLiveIntentsFilter || {},
  sort: getters.getSort || {
    columnName: METRIC_NAMES.CONVERSATIONS_RESPONSE_TIME,
    direction: SORT_DIRECTION.ASCENDING,
  },
});

export default {
  getLiveIntentsDataStatus,
  getLiveIntentsFilter,
  getLiveIntents,
  getTimeFrameFilter,
  getUserIds,
  getFilteredGroupsIds,
  getBlockConsumerName,
  getUsers,
  getConversations,
  getAgentsFilter,
  getStatusFilter,
  getSort,
  getSkillsFilter,
  getGlobalFiltersUpdating,
  getMetricAttributes,
  isDataObsolete,
  lastTimeDataUpdated,
  dataHasBeenFetched,
  isWaitingForData,
  conversationsApiIsDown,
  getHourFormatSiteSetting,
  getLoggedInUserId,
  getAccountId,
  hasContactCenterFeature,
  hasLiveIntent,
  isIntentsFilterEnabled,
  getConversationStatus,
  getPresetValues,
};
